import { apiClient } from './client';

// Budgeting (422477): one budget per financial year — amount per account × cost centre × period —
// and the Actual vs Budget P&L measured against the approved one.

export interface BudgetPeriod { periodNumber: number; periodCode: string; periodName: string }
export interface BudgetOption { id: string; code: string; name: string; group: string | null }
export interface BudgetRow { accountId: string; costCenterId: string; amounts: number[] }

export interface Budget {
  financialYearCode: string;
  id: string | null;
  name: string | null;
  status: 'DRAFT' | 'APPROVED' | null;
  approvedDate: string | null;
  periods: BudgetPeriod[];
  rows: BudgetRow[];
  accounts: BudgetOption[];
  costCenters: BudgetOption[];
}

export interface BudgetVsActualRow {
  rowType: 'ACCOUNT' | 'SUBTOTAL' | 'NET';
  section: string;
  code: string | null;
  name: string;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number | null;
}

export interface BudgetVsActual {
  financialYearCode: string;
  fromPeriod: string;
  toPeriod: string;
  budgetStatus: string | null;
  notice: string | null;
  rows: BudgetVsActualRow[];
}

export interface BudgetVsActualFilter {
  fromPeriod?: number;
  toPeriod?: number;
  costCenterIds?: string[];
  accountId?: string;
}

const unwrap = <T,>(r: { data: { data: T } }) => r.data.data;

export const budgetApi = {
  get: async (yearCode: string) =>
    unwrap(await apiClient.get<{ data: Budget }>(`/budgets/${encodeURIComponent(yearCode)}`)),
  save: async (yearCode: string, name: string | null, rows: BudgetRow[]) =>
    unwrap(await apiClient.put<{ data: Budget }>(`/budgets/${encodeURIComponent(yearCode)}`, { name, rows })),
  approve: async (yearCode: string) =>
    unwrap(await apiClient.post<{ data: Budget }>(`/budgets/${encodeURIComponent(yearCode)}/approve`)),
  revise: async (yearCode: string) =>
    unwrap(await apiClient.post<{ data: Budget }>(`/budgets/${encodeURIComponent(yearCode)}/revise`)),
  vsActual: async (yearCode: string, filter: BudgetVsActualFilter) =>
    unwrap(await apiClient.get<{ data: BudgetVsActual }>(`/budgets/${encodeURIComponent(yearCode)}/vs-actual`, {
      params: filter,
      // costCenterIds=a&costCenterIds=b — what ASP.NET binds to a List<Guid>
      paramsSerializer: { indexes: null },
    })),
};
